import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../Button/Button';
import Modal, { ModalBody, ModalFooter } from '../Modal';
import { Checkbox, Textarea } from '@rebass/forms';
import { Input, Label, Select as FormSelect } from '../Form';
import { useMutation } from '@apollo/client';
import useMode from '../../hooks/useMode';
import useNotify from '../../hooks/useNotify';
import { Customer, CustomerInput } from './types';
import FormRow from '../Form/FormRow';
import Form from '../Form/Form';
import FormColumn from '../Form/FormColumn';
import { ContactProfileInput, profileTypes } from '../Profile/types';
import Perm from '../../types/perm';
import ContactProfileForm from '../Profile/ContactProfileForm';
import { Flex } from 'rebass';
import {
  CUSTOMER_FIELDS_WATER_SUPPLIER,
  gql as waterSupplier_gql,
} from '@/modes/waterSupplier/gql';
import {
  gql as company_gql,
  CUSTOMER_FIELDS_COMPANY,
} from '@/modes/company/gql';

type CustomerEditModalProps = {
  onSave?: (customer: Customer) => void;
  onClose?: () => void;
  serviceAddressId?: string;
  waterServiceId?: string;
  perms?: Perm[];
} & (
  | {
      action: 'add';
      customer?: Partial<
        Omit<CustomerInput, 'profile'> & {
          profile?: Partial<CustomerInput['profile']>;
        }
      >;
    }
  | {
      action: 'edit';
      customer: CustomerInput;
      perms: Perm[];
    }
);

const CREATE_CUSTOMER_WATER_SUPPLIER = waterSupplier_gql(
  `#graphql
    mutation CreateCustomer($input: CustomerInput!, $serviceAddressId: String, $waterServiceId: String) {
      createCustomer(
        input: $input,
        serviceAddressId: $serviceAddressId,
        waterServiceId: $waterServiceId
      ) {
        ...CustomerFields
      }
    }
  `,
  [CUSTOMER_FIELDS_WATER_SUPPLIER]
);

const UPDATE_CUSTOMER_WATER_SUPPLIER = waterSupplier_gql(
  `#graphql
  mutation UpdateCustomer($customerId: ID!, $input: CustomerInput!) {
    updateCustomer(customerId: $customerId, input: $input) {
      ...CustomerFields
    }
  }
`,
  [CUSTOMER_FIELDS_WATER_SUPPLIER]
);

const CREATE_CUSTOMER_COMPANY = company_gql(
  `#graphql
  mutation CreateCustomer($input: CustomerInput!, $serviceAddressId: String, $waterServiceId: String) {
    createCustomer(
      input: $input,
      serviceAddressId: $serviceAddressId,
      waterServiceId: $waterServiceId
    ) {
      ...CustomerFields
    }
  }
`,
  [CUSTOMER_FIELDS_COMPANY]
);

const UPDATE_CUSTOMER_COMPANY = company_gql(
  `#graphql
mutation UpdateCustomer($customerId: ID!, $input: CustomerInput!) {
  updateCustomer(customerId: $customerId, input: $input) {
    ...CustomerFields
  }
}
`,
  [CUSTOMER_FIELDS_COMPANY]
);

const CustomerEditModal: React.FC<CustomerEditModalProps> = ({
  onClose,
  onSave,
  customer,
  serviceAddressId,
  waterServiceId,
  action,
  perms,
}) => {
  const mode = useMode();
  const { showError, showSuccess } = useNotify();

  const [createCustomer] = useMutation<{
    createCustomer: Customer;
  }>(
    mode === 'water-supplier'
      ? CREATE_CUSTOMER_WATER_SUPPLIER
      : CREATE_CUSTOMER_COMPANY,
    {
      onError: (error) => {
        showError('Error creating customer', error);
        console.error('Error creating customer', error);
      },
      onCompleted: (data) => {
        showSuccess('New customer was successfully saved!');
        onSave?.(data.createCustomer);
        onClose?.();
      },
    }
  );

  const [updateCustomer] = useMutation<{
    updateCustomer: Customer;
  }>(
    mode === 'water-supplier'
      ? UPDATE_CUSTOMER_WATER_SUPPLIER
      : UPDATE_CUSTOMER_COMPANY,
    {
      onError: (error) => {
        showError('Error updating customer', error);
        console.error('Error updating customer', error);
      },
      onCompleted: (data) => {
        showSuccess('Customer changes saved!');
        onSave?.(data.updateCustomer);
        onClose?.();
      },
    }
  );

  const [customerInput, setCustomerInput] = useState<CustomerInput>(
    customer
      ? {
          accountNumber: customer.accountNumber,
          textEnabled: customer.textEnabled ?? false,
          mailEnabled: customer.mailEnabled ?? true,
          emailEnabled: customer.emailEnabled ?? false,
          status: customer.status || 'ACTIVE',
          notes: customer.notes || '',
          profile: customer.profile
            ? {
                type: customer.profile.type || 'INDIVIDUAL',
                name: customer.profile.name ?? '',
                firstName: customer.profile.firstName ?? '',
                lastName: customer.profile.lastName ?? '',
                phones:
                  customer.profile.phones?.map((phone) => ({
                    subType: phone.subType,
                    value: phone.value,
                    extension: phone.extension,
                  })) ?? [],
                emails:
                  customer.profile.emails?.map((email) => ({
                    subType: email.subType,
                    value: email.value,
                  })) ?? [],
                contactAddressId: customer.profile.contactAddressId,
                billingAddressId: customer.profile.billingAddressId,
              }
            : {
                type: 'INDIVIDUAL',
                name: '',
                firstName: '',
                lastName: '',
                phones: [],
                emails: [],
              },
        }
      : {
          status: 'ACTIVE',
          notes: '',
          textEnabled: false,
          mailEnabled: true,
          emailEnabled: false,
          profile: {
            type: 'INDIVIDUAL',
            name: '',
            firstName: '',
            lastName: '',
            phones: [],
            emails: [],
          },
        }
  );

  const save = useCallback(() => {
    if (action === 'add') {
      createCustomer({
        variables: { input: customerInput, serviceAddressId, waterServiceId },
      });
    } else if (action === 'edit') {
      updateCustomer({
        variables: { customerId: customer?.id, input: customerInput },
      });
    }
  }, [action, customer?.id, customerInput, createCustomer, updateCustomer]);

  const handleCustomerObjChange = (e: {
    target: HTMLInputElement | HTMLTextAreaElement;
  }) => {
    const { name, type } = e.target;
    let value: boolean | string = e.target.value;
    if (type === 'checkbox') {
      value = (e.target as HTMLInputElement).checked;
    }

    setCustomerInput((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleCustomerProfileObjChange = (e: any) => {
    const { name, value } = e.target;

    setCustomerInput((prevState) => ({
      ...prevState,
      profile: {
        ...prevState.profile,
        [name]: value,
      },
    }));
  };

  const onProfileChange = (profile: ContactProfileInput) => {
    setCustomerInput((prevState) => ({
      ...prevState,
      profile: {
        ...prevState.profile,
        ...profile,
      },
    }));
  };

  const canEditProfile = action === 'add' || perms.includes('UPDATE');
  const readOnly = action === 'edit' && !canEditProfile;

  return (
    <Modal
      title={action === 'edit' ? 'Edit Customer' : 'Add Customer'}
      onClose={onClose}
    >
      <ModalBody>
        <Form>
          <FormRow>
            {!canEditProfile && (
              <FormColumn>
                <Label>Name</Label>
                {customerInput.profile.name}
              </FormColumn>
            )}
            {canEditProfile && (
              <FormColumn>
                <Label>Type</Label>
                <FormSelect
                  name="type"
                  onChange={handleCustomerProfileObjChange}
                  value={customerInput.profile.type}
                  width="100%"
                  disabled={readOnly}
                >
                  {!customer && (
                    <option value="" disabled hidden>
                      Select Type
                    </option>
                  )}
                  {profileTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </FormSelect>
              </FormColumn>
            )}
            <FormColumn>
              <Label>Account Number</Label>
              <Input
                name="accountNumber"
                onChange={handleCustomerObjChange}
                type="text"
                value={customerInput.accountNumber || ''}
                width="100%"
                autoComplete="off"
                disabled={readOnly}
              />
            </FormColumn>
          </FormRow>
          {canEditProfile && (
            <ContactProfileForm
              onChange={onProfileChange}
              value={customerInput.profile}
              disabled={readOnly}
              show={[
                'name',
                'contactAddressId',
                'billingAddressId',
                'emails',
                'phones',
              ]}
            />
          )}
          {/*<Flex*/}
          {/*  flexDirection="column"*/}
          {/*  flex={1}*/}
          {/*  mt={3}*/}
          {/*  sx={{*/}
          {/*    '@media screen and (max-width:64em)': { mr: 0 },*/}
          {/*  }}*/}
          {/*>*/}
          {/*  <Label>Tags</Label>*/}
          {/*  <Select isMulti options={mockOptions} />*/}
          {/*</Flex>*/}
          <FormColumn>
            <Label>Communication Preferences</Label>
            <Flex
              justifyContent="flex-start"
              sx={{ gap: 8, label: { width: 'auto' } }}
            >
              <Label>
                <Checkbox
                  name="textEnabled"
                  checked={customerInput.textEnabled}
                  onChange={handleCustomerObjChange}
                  disabled={readOnly}
                />
                Text
              </Label>
              <Label>
                <Checkbox
                  name="emailEnabled"
                  checked={customerInput.emailEnabled}
                  onChange={handleCustomerObjChange}
                  disabled={readOnly}
                />
                Email
              </Label>
              <Label>
                <Checkbox
                  name="mailEnabled"
                  checked={customerInput.mailEnabled}
                  onChange={handleCustomerObjChange}
                  disabled={readOnly}
                />
                Mail
              </Label>
            </Flex>
          </FormColumn>
          <FormColumn>
            <Label>Notes</Label>
            <Textarea
              height={16 * 3 + 2}
              id="notes"
              name="notes"
              value={customerInput.notes || ''}
              onChange={handleCustomerObjChange}
              disabled={readOnly}
            />
          </FormColumn>
        </Form>
      </ModalBody>
      <ModalFooter>
        {!readOnly && (
          <Button
            icon="save"
            disabled={
              customerInput.profile.type === 'INDIVIDUAL'
                ? !customerInput.profile.firstName ||
                  !customerInput.profile.lastName
                : !customerInput.profile.name
            }
            onClick={save}
          >
            Save
          </Button>
        )}
        <Button variant="outline" icon="slash-circle" onClick={onClose}>
          {readOnly ? 'Close' : 'Cancel'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

CustomerEditModal.propTypes = {
  onClose: PropTypes.any,
  customer: PropTypes.any,
  action: PropTypes.any,
};

export default CustomerEditModal;
